import { BaseService, type ResponseMessage } from "@kiddocare-platform/portal-common/base";
import type { Instance } from "@kiddocare-platform/portal-common/core";
import type { Invoice, ParentAccountCancellationCode, PaymentSummary, Permission, Subscription } from "@kiddocare-platform/portal-common/billing";

export class BillingHandlingService extends BaseService {

  public async Check(): Promise<ResponseMessage<Permission>> {
    try {
      const response = await this.AxiosInstance.get(`/parent/handling/subscriptions/check`);
      return response.data;
    }
    catch (error: any) {
      this.log.error("Check", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async ManageSubscription(): Promise<ResponseMessage<{
    id?: string;
    object?: string;
    configuration?: string;
    created?: bigint;
    customer?: string;
    flow?: unknown;
    livemode?: boolean;
    locale?: unknown;
    on_behalf_of?: unknown;
    return_url?: string;
    url?: string;
  }>> {
    try {
      const response = await this.AxiosInstance.get(`/parent/handling/subscriptions/manageSubscription`);
      return response.data;
    }
    catch (error: any) {
      this.log.error("ManageSubscription", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async Search(query: { page: number, size: number }): Promise<ResponseMessage<Array<Instance>>> {
    try {
      const response = await this.AxiosInstance.post(`/parent/handling/payments/search`, query);
      return response.data;
    }
    catch (error: any) {
      this.log.error("Search", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async SearchSubscription(query: { page: number, size: number }): Promise<ResponseMessage<Array<Subscription>>> {
    try {
      const response = await this.AxiosInstance.post(`/parent/handling/subscriptions/search`, query);
      return response.data;
    }
    catch (error: any) {
      this.log.error("SearchSubscription", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async StartVisitPayment(ticket: string): Promise<ResponseMessage<{ client_secret: string, amount: number }>> {
    try {
      const response = await this.AxiosInstance.get(`/parent/handling/payments/startVisitPayment/${ticket}`);
      return response.data;
    }
    catch (error: any) {
      this.log.error("StartVisitPayment", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async StartCertifiedPayment(ticket: string): Promise<ResponseMessage<{ client_secret: string, amount: number }>> {
    try {
      const response = await this.AxiosInstance.get(`/parent/handling/payments/startCertifiedPayment/${ticket}`);
      return response.data;
    }
    catch (error: any) {
      this.log.error("StartCertifiedPayment", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async GetInvoicesByTicket(ticket: string): Promise<ResponseMessage<Array<Invoice>>> {
    try {
      const response = await this.AxiosInstance.get(`/parent/handling/payments/getInvoicesByTicket/${ticket}`);
      return response.data;
    }
    catch (error: any) {
      this.log.error("GetInvoicesByTicket", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async GetInvoicesById(idInvoice: bigint): Promise<any> {
    try {
      const response = await this.AxiosInstance.get(`/parent/handling/payments/getInvoicesById/${idInvoice}`, {
        responseType: 'arraybuffer'
      });
      return response.data;
    } catch (error: any) {
      this.log.error("GetInvoice", error.stack, JSON.stringify(error));
      if (error.response?.data instanceof ArrayBuffer) {
        const contentType = error.response.headers['content-type'];
        if (contentType?.includes('application/json')) {
          const decoder = new TextDecoder('utf-8');
          const jsonString = decoder.decode(error.response.data);
          const parsed = JSON.parse(jsonString);
          throw parsed;
        }
      }
      throw error;
    }
  }

  public async GetToPayInstancesList(): Promise<ResponseMessage<Array<Instance>>> {
    try {
      const response = await this.AxiosInstance.get(`/parent/handling/payments/getToPayInstancesList`);
      return response.data;
    }
    catch (error: any) {
      this.log.error("GetToPayInstancesList", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async GetToPayInstancesCount(): Promise<ResponseMessage<{ count: number }>> {
    try {
      const response = await this.AxiosInstance.get(`/parent/handling/payments/getToPayInstancesCount`);
      return response.data;
    }
    catch (error: any) {
      this.log.error("GetToPayInstancesCount", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async GetInvoiceById(idInvoice: bigint): Promise<Blob> {
    try {
      const response = await this.AxiosInstance.get(`/parent/handling/subscriptions/getInvoiceById/${idInvoice}`, {
        responseType: "blob",
      });
      return response.data;
    } catch (error: any) {
      this.log.error("GetInvoiceById", error?.stack ?? String(error));
      throw error;
    }
  }

  public async Delete(code: ParentAccountCancellationCode): Promise<ResponseMessage<any>> {
    try {
      const response = await this.AxiosInstance.delete(`/parent/handling/accounts/delete`, { data: { code } });
      return response.data;
    } catch (error: any) {
      this.log.error("DeleteAccount", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async GetCertifiedPaymentByTicket(ticket: string): Promise<ResponseMessage<PaymentSummary>> {
    try {
      const response = await this.AxiosInstance.get(`/parent/handling/payments/getCertifiedPaymentByTicket/${ticket}`);
      return response.data;
    } catch (error: any) {
      this.log.error("GetCertifiedPaymentByTicket", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async GetVisitPaymentByTicket(ticket: string): Promise<ResponseMessage<PaymentSummary>> {
    try {
      const response = await this.AxiosInstance.get(`/parent/handling/payments/getVisitPaymentByTicket/${ticket}`);
      return response.data;
    } catch (error: any) {
      this.log.error("GetVisitPaymentByTicket", error.stack, JSON.stringify(error));
      throw error;
    }
  }

}